import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { APP_MENU_MAP, NavMenuItem, USER_PERMISSIONS } from '@siat-fe/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  map,
  Observable,
  shareReplay,
} from 'rxjs';
import { StrutturaUser } from '../models/userinfo.model';
import { GetSelectedAppId, SetSelectedAppId } from './storage-data.utility';
import { UserProvider } from './user.provider';

/**
 * Service responsible for managing the application navigation menu.
 * It handles filtering based on permissions, dynamic parameter replacement,
 * and state management for the selected application context.
 */
@Injectable({
  providedIn: 'root',
})
export class MenuNavigationService {
  /**
   * The user provider service, used to retrieve user permissions and selected structure.
   */
  private userProvider = inject(UserProvider);

  /**
   * The Angular Router service, used to track navigation events.
   */
  private router = inject(Router);

  /**
   * BehaviorSubject that holds the ID of the currently selected application (e.g., 'home', 'amministrazione').
   * Initialized with the value retrieved from local storage.
   */
  private _selectedAppId = new BehaviorSubject<string | null>(
    GetSelectedAppId()
  );

  /**
   * Observable stream of the selected application ID.
   */
  readonly selectedAppId$ = this._selectedAppId.asObservable();

  /**
   * Observable stream of available application IDs (placeholder for future use).
   */
  readonly availableAppIds$: Observable<string[]>;

  /**
   * Observable stream of the processed menu items for the currently selected app.
   * This stream combines the App ID, User Permissions, and Selected Structure to produce
   * a filtered, sorted, and customized menu list.
   */
  readonly currentAppMenuItems$: Observable<NavMenuItem[]> = combineLatest([
    this.selectedAppId$,
    this.userProvider.permissionList$,
    this.userProvider.selectedStruttura$,
  ]).pipe(
    debounceTime(10),
    map(([appId, userPermissions, selectedStruttura]) => {
      if (!appId || !userPermissions) return [];

      const rawMenuItems = APP_MENU_MAP[appId] || [];
      const menuItemsCopy: NavMenuItem[] = JSON.parse(
        JSON.stringify(rawMenuItems)
      );

      return this.processMenuItems(
        menuItemsCopy,
        userPermissions,
        selectedStruttura,
        appId
      );
    }),
    // Caches the latest result so late subscribers (like the Sidebar) get data immediately.
    shareReplay(1)
  );

  constructor() {
    this.subscribeToRouterEvents();
  }

  /**
   * Listens to router navigation events to automatically update the selected App ID
   * based on the URL.
   */
  private subscribeToRouterEvents(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event: NavigationEnd) => {
        const appId = this.getAppIdFromUrl(event.urlAfterRedirects);
        if (appId) this.setSelectedApp(appId);
      });
  }

  /**
   * Extracts the application ID from the current URL string.
   * @param url The current URL.
   * @returns The App ID string or null if not found.
   */
  private getAppIdFromUrl(url: string): string | null {
    if (url.includes('/amministrazione')) return 'amministrazione';
    if (url.includes('/home')) return 'home';
    return null;
  }

  /**
   * Orchestrates the entire pipeline of menu manipulation:
   * 1. Filtering by permissions.
   * 2. Replacing dynamic URL parameters.
   * 3. Updating titles/icons for specific roles.
   * 4. Sorting the homepage.
   */
  private processMenuItems(
    items: NavMenuItem[],
    userPermissions: string[],
    selectedStruttura: StrutturaUser | null,
    appId: string
  ): NavMenuItem[] {
    const isStructureOnlyHomeUser =
      appId === 'home' && this.isStructureOnlyUser(userPermissions);

    const filtered = this.filterMenuRecursive(
      items,
      userPermissions,
      isStructureOnlyHomeUser
    );

    const withHrefs = this.replaceDynamicParamsRecursive(
      filtered,
      selectedStruttura
    );

    const withTitles = this.updateHomepageTitleForOperativo(
      withHrefs,
      userPermissions
    );

    const restrictedForStructureOnlyUser =
      appId === 'home'
        ? this.restrictHomeMenuForStructureOnlyUser(withTitles, userPermissions)
        : withTitles;

    return this.sortHomepageFirst(
      restrictedForStructureOnlyUser,
      userPermissions
    );
  }

  private restrictHomeMenuForStructureOnlyUser(
    items: NavMenuItem[],
    userPermissions: string[]
  ): NavMenuItem[] {
    if (!this.isStructureOnlyUser(userPermissions)) return items;

    return items.filter(
      item =>
        item.isSpecialAction ||
        item.href === '/home/dashboard' ||
        item.href === '/home/struttura'
    );
  }

  /**
   * Recursively replaces dynamic parameters (e.g., :strutturaId) in menu item hrefs
   * with actual values from the selected structure.
   */
  private replaceDynamicParamsRecursive(
    items: NavMenuItem[],
    selectedStruttura: StrutturaUser | null
  ): NavMenuItem[] {
    return items.map(item => {
      if (item.href && item.href.includes(':strutturaId')) {
        const strutturaId = selectedStruttura?.id?.toString() || '';
        item.href = item.href.replace(':strutturaId', strutturaId);
      }

      if (item.subItems && item.subItems.length > 0) {
        item.subItems = this.replaceDynamicParamsRecursive(
          item.subItems,
          selectedStruttura
        );
      }
      return item;
    });
  }

  /**
   * Recursively filters menu items and their sub-items based on the user's permissions list.
   * Removes items where the user lacks access and cleans up empty groups.
   */
  private filterMenuRecursive(
    items: NavMenuItem[],
    permissions: string[],
    allowStructureOnlyDashboard = false
  ): NavMenuItem[] {
    return items
      .filter(item =>
        this.hasAccess(item, permissions, allowStructureOnlyDashboard)
      )
      .map(item => {
        if (item.subItems && item.subItems.length > 0) {
          return {
            ...item,
            subItems: this.filterMenuRecursive(
              item.subItems,
              permissions,
              allowStructureOnlyDashboard
            ),
          };
        }
        return item;
      })
      .filter(
        item =>
          !item.subItems ||
          item.subItems.length > 0 ||
          (item.href && item.href !== '')
      );
  }

  /**
   * Modifies the title and icon of the 'operativo' homepage (Calendar)
   * ONLY if the user has the 'operativo' role but NOT the 'coordinatore' role.
   */
  private updateHomepageTitleForOperativo(
    items: NavMenuItem[],
    userPermissions: string[]
  ): NavMenuItem[] {
    const isCoordinatore = userPermissions.includes(
      USER_PERMISSIONS.coordinatore
    );
    const isOperativo = userPermissions.includes(USER_PERMISSIONS.operativo);
    const isOnlyOperativo = isOperativo && !isCoordinatore;

    if (!isOnlyOperativo) return items;

    return items.map(item => {
      if (item.homepageForPermission === USER_PERMISSIONS.operativo) {
        return {
          ...item,
          title: 'MENU.homepage', // Use translation key
          icon: 'home', // Force icon change to Home
        };
      }
      return item;
    });
  }

  /**
   * Checks if a user has at least one of the required permissions for a specific menu item.
   */
  private hasAccess(
    item: NavMenuItem,
    userPermissions: string[],
    allowStructureOnlyDashboard = false
  ): boolean {
    if (!item.permissions || item.permissions.length === 0) {
      return true;
    }

    if (allowStructureOnlyDashboard && item.href === '/home/dashboard') {
      return true;
    }

    return item.permissions.some(requiredPermission =>
      userPermissions.includes(requiredPermission)
    );
  }

  private isStructureOnlyUser(userPermissions: string[]): boolean {
    return (
      !userPermissions.includes(USER_PERMISSIONS.coordinatore) &&
      !userPermissions.includes(USER_PERMISSIONS.operativo) &&
      userPermissions.length > 0
    );
  }

  /**
   * Sorts the menu items to ensure the correct homepage appears at the top of the list
   * based on the user's priority role (Coordinator > Operativo).
   */
  private sortHomepageFirst(
    items: NavMenuItem[],
    userPermissions: string[]
  ): NavMenuItem[] {
    const priorityPermission = userPermissions.includes(
      USER_PERMISSIONS.coordinatore
    )
      ? USER_PERMISSIONS.coordinatore
      : USER_PERMISSIONS.operativo;

    const homeIndex = items.findIndex(
      item => item.homepageForPermission === priorityPermission
    );

    if (homeIndex > -1) {
      const sortedItems = [...items];
      const [homeItem] = sortedItems.splice(homeIndex, 1);
      sortedItems.unshift(homeItem);
      return sortedItems;
    }

    return items;
  }

  /**
   * Updates the selected application ID and persists it to storage.
   * @param appId The new Application ID to select.
   */
  setSelectedApp(appId: string): void {
    if (this._selectedAppId?.value !== appId) {
      SetSelectedAppId(appId);
      this._selectedAppId.next(appId);
    }
  }
}
